import type { SeenMedplumClient } from '@seen/core/client'
import { invariant } from '@seen/core/invariant'
import axiosLib, { type InternalAxiosRequestConfig } from 'axios'
import { type JSX, type ReactNode, useCallback, useMemo } from 'react'
import { refreshMedplumIfExpired } from '#src/components/liveblocksMedplumRefresh.ts'
import {
  type SeenAiApi,
  SeenApiContext,
  type SeenNpiApi,
  type SeenPdfApi,
} from '#src/contexts/seen-api/SeenApiContext.tsx'

export function SeenApiContextProvider({
  baseURL,
  medplum,
  appVersion,
  appCommitSha,
  ai,
  npi,
  pdf,
  children,
}: {
  baseURL: string
  medplum?: SeenMedplumClient
  appVersion: string
  appCommitSha: string
  // AI capability adapter from the host app. Omit when the app does not serve
  // AI routes; `aiEnabled` on the context derives from its presence.
  ai?: SeenAiApi
  // NPI-registry capability adapter; omit when the app has no NPPES proxy.
  npi?: SeenNpiApi
  // PDF export adapter; omit when the app does not serve /api/pdf.
  pdf?: SeenPdfApi
  children: ReactNode
}): JSX.Element {
  const addMedplumAccessToken = useCallback(
    async (request: InternalAxiosRequestConfig) => {
      invariant(medplum, 'medplum is required')
      await refreshMedplumIfExpired(medplum)
      request.headers.Authorization = `Bearer ${medplum.getAccessToken()}`
      if (medplum.traceId) {
        request.headers['X-Trace-ID'] = medplum.traceId
      }
      return request
    },
    [medplum]
  )

  const seenApi = useMemo(() => {
    const api = axiosLib.create({ baseURL })
    if (medplum) {
      api.interceptors.request.use(addMedplumAccessToken)
    }
    return api
  }, [baseURL, medplum, addMedplumAccessToken])

  const setMedplumAccessToken = useCallback(
    (accessToken: string | undefined): void => {
      if (!accessToken) {
        delete seenApi.defaults.headers.common.Authorization
        return
      }

      seenApi.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    },
    [seenApi]
  )

  const value = useMemo(
    () => ({
      seenApi,
      ai,
      aiEnabled: ai !== undefined,
      npi,
      pdf,
      setMedplumAccessToken,
      appVersion,
      appCommitSha,
    }),
    [seenApi, ai, npi, pdf, setMedplumAccessToken, appVersion, appCommitSha]
  )

  return (
    <SeenApiContext.Provider value={value}>{children}</SeenApiContext.Provider>
  )
}
